import React, { useState } from 'react';
import { Container, Row, Col, Card, Form, Button, Alert, Badge, Spinner } from 'react-bootstrap';
import { useAnalytics } from './hooks/useAnalytics';
import QueryConfiguration from './components/QueryConfiguration';
import ResultsTable from './components/ResultsTable';
import Pagination from '../../components/Global/Pagination';
import ClientSearchModal from '../../components/Global/ClientSearchModal';
import { exportToExcel } from './utils/excelExport';
import { hasIncompleteFilters, getColumnDatabaseName, getDynamicColumns } from './utils/analyticsUtils';

const AnalyticsPage = () => {
  const {
    // State
    query,
    results,
    loading,
    error,
    tables,
    selectedTable,
    selectedTableInfo,
    dropdownOptions,
    clientNames,
    currentPage,
    itemsPerPage,
    totalCount,
    executedDimension,
    executedTimeFrame,
    expandedRows,
    isExporting,
    
    // Setters
    setQuery,
    setSelectedTable,
    setExpandedRows,
    setIsExporting,
    setClientNames,
    
    // Actions
    executeQuery,
    getAllData,
    resetAll,
    loadClientName
  } = useAnalytics();

  const [showClientModal, setShowClientModal] = useState(false);
  const [clientModalFilterIndex, setClientModalFilterIndex] = useState(null);

  // Query handlers
  const handleInputChange = (field, value) => {
    setQuery(prev => ({ ...prev, [field]: value }));
  };

  const addFilter = (groupIndex = 0) => {
    setQuery(prev => {
      const newFilterGroups = [...prev.filterGroups];
      if (!newFilterGroups[groupIndex]) {
        newFilterGroups[groupIndex] = { filters: [], logicalOperator: groupIndex > 0 ? 0 : null };
      }
      newFilterGroups[groupIndex].filters.push({ fieldName: '', operator: 0, value: '', logicalOperator: null });
      return { ...prev, filterGroups: newFilterGroups };
    });
  };

  const addFilterGroup = () => {
    setQuery(prev => ({
      ...prev,
      filterGroups: [...prev.filterGroups, { filters: [{ fieldName: '', operator: 0, value: '', logicalOperator: null }], logicalOperator: 0 }]
    }));
  };

  const updateFilter = (groupIndex, filterIndex, field, value) => {
    setQuery(prev => {
      const newFilterGroups = [...prev.filterGroups];
      const updatedFilter = { ...newFilterGroups[groupIndex].filters[filterIndex], [field]: value };
      if (field === 'operator' && (value === 8 || value === 9)) {
        updatedFilter.value = '';
      }
      newFilterGroups[groupIndex].filters[filterIndex] = updatedFilter;
      return { ...prev, filterGroups: newFilterGroups };
    });
  };

  const removeFilter = (groupIndex, filterIndex) => {
    setQuery(prev => {
      const newFilterGroups = [...prev.filterGroups];
      newFilterGroups[groupIndex].filters.splice(filterIndex, 1);
      if (newFilterGroups[groupIndex].filters.length === 0) {
        newFilterGroups.splice(groupIndex, 1);
      }
      return { ...prev, filterGroups: newFilterGroups };
    });
  };

  const updateGroupLogicalOperator = (groupIndex, logicalOperator) => {
    setQuery(prev => {
      const newFilterGroups = [...prev.filterGroups];
      newFilterGroups[groupIndex].logicalOperator = logicalOperator;
      return { ...prev, filterGroups: newFilterGroups };
    });
  };

  // Client modal handlers
  const handleClientSelect = (client) => {
    if (clientModalFilterIndex !== null) {
      const { groupIndex, filterIndex } = clientModalFilterIndex;
      const clientId = client.id.toString();
      updateFilter(groupIndex, filterIndex, 'value', clientId);
      setClientNames(prev => ({
        ...prev,
        [clientId]: `${client.name} ${client.lastName1}`.trim()
      }));
      setShowClientModal(false);
      setClientModalFilterIndex(null);
    }
  };

  const openClientModal = (groupIndex, filterIndex) => {
    setClientModalFilterIndex({ groupIndex, filterIndex });
    setShowClientModal(true);
  };

  const getSelectedClientName = (clientId) => {
    if (!clientId) return '';
    return clientNames[clientId] || `Cliente ID: ${clientId}`;
  };

  const handleClientIdChange = (groupIndex, filterIndex, value) => {
    updateFilter(groupIndex, filterIndex, 'value', value);
    if (value && /^\d+$/.test(value)) {
      loadClientName(value);
    }
  };

  // Utility functions
  const getFilterColumnInfo = (fieldName) => {
    const filtersColumns = selectedTableInfo?.columns?.filters || [];
    return filtersColumns.find(col => col.name === fieldName);
  };

  const toggleRowExpansion = (rowKey) => {
    setExpandedRows(prev => {
      const newSet = new Set(prev);
      if (newSet.has(rowKey)) {
        newSet.delete(rowKey);
      } else {
        newSet.add(rowKey);
      }
      return newSet;
    });
  };

  const handlePageChange = (page) => {
    executeQuery(page);
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    executeQuery(1, newItemsPerPage);
  };

  const handleExportToExcel = () => {
    exportToExcel(
      getAllData,
      executedTimeFrame,
      executedDimension,
      selectedTableInfo,
      selectedTable,
      setIsExporting
    );
  };

  const handleSort = (column) => {
    const dynamicColumns = getDynamicColumns(results.items || results);
    const dbColumnName = getColumnDatabaseName(column, dynamicColumns, executedDimension);
    
    const newDirection = query.sortColumn === dbColumnName && query.sortDirection === 'asc' ? 'desc' : 'asc';
    
    const newQuery = {
      ...query,
      sortColumn: dbColumnName,
      sortDirection: newDirection
    };
    
    setQuery(newQuery);
    executeQuery(1, null, newQuery);
  };

  return (
    <div style={{ backgroundColor: '#f8f9fa', minHeight: '100vh' }}>
      <Container fluid className="py-4">
        <div className="mb-4">
          <h2 className="fw-bold text-dark mb-1">Analytics</h2>
        </div>

        <Card className="shadow-sm border-0 mb-4">
          <Card.Body>
            <h6 className="fw-semibold mb-3">Fuente de Datos</h6>
            <Form.Select 
              value={selectedTable} 
              onChange={(e) => setSelectedTable(e.target.value)}
              size="lg"
            >
              <option value="">Seleccionar tabla de datos...</option>
              {tables.map(table => (
                <option key={table.name} value={table.name}>
                  {table.displayName || table.name}
                </option>
              ))}
            </Form.Select>
          </Card.Body>
        </Card>

        <Row>
          <Col md={4}>
            <Card className="shadow-sm border-0">
              <Card.Header className="bg-white py-3">
                <h6 className="mb-0 fw-semibold">Configuración</h6>
              </Card.Header>
              <Card.Body>
                <QueryConfiguration
                  query={query}
                  selectedTable={selectedTable}
                  selectedTableInfo={selectedTableInfo}
                  loading={loading}
                  dropdownOptions={dropdownOptions}
                  clientNames={clientNames}
                  onInputChange={handleInputChange}
                  onAddFilter={addFilter}
                  onAddFilterGroup={addFilterGroup}
                  onUpdateFilter={updateFilter}
                  onRemoveFilter={removeFilter}
                  onUpdateGroupLogicalOperator={updateGroupLogicalOperator}
                  onExecuteQuery={executeQuery}
                  onResetAll={resetAll}
                  onOpenClientModal={openClientModal}
                  onHandleClientIdChange={handleClientIdChange}
                  getSelectedClientName={getSelectedClientName}
                  getFilterColumnInfo={getFilterColumnInfo}
                />
              </Card.Body>
            </Card>
          </Col>

          <Col md={8}>
            <Card className="shadow-sm border-0">
              <Card.Header className="bg-white py-3">
                <div className="d-flex justify-content-between align-items-center">
                  <h6 className="mb-0 fw-semibold">Resultados</h6>
                  <div className="d-flex align-items-center gap-2">
                    {results.length > 0 && (
                      <>
                        <Button
                          variant="success"
                          size="sm"
                          onClick={handleExportToExcel}
                          disabled={isExporting || loading}
                        >
                          {isExporting ? (
                            <>
                              <Spinner animation="border" size="sm" className="me-2" />
                              Exportando...
                            </>
                          ) : (
                            <>
                              📊 Exportar Excel
                            </>
                          )}
                        </Button>
                        <Badge bg="primary">{totalCount.toLocaleString()} registros</Badge>
                      </>
                    )}
                  </div>
                </div>
              </Card.Header>
              <Card.Body>
                {error && <Alert variant="danger">{error}</Alert>}
                
                {results.length > 0 ? (
                  <>
                    <ResultsTable
                      results={results}
                      executedTimeFrame={executedTimeFrame}
                      executedDimension={executedDimension}
                      expandedRows={expandedRows}
                      onToggleRowExpansion={toggleRowExpansion}
                      sortColumn={query.sortColumn}
                      sortDirection={query.sortDirection}
                      onSort={handleSort}
                      executedDimension={executedDimension}
                    />
                    <Pagination
                      totalCount={totalCount}
                      currentPage={currentPage}
                      itemsPerPage={itemsPerPage}
                      onPageChange={handlePageChange}
                      onItemsPerPageChange={handleItemsPerPageChange}
                    />
                  </>
                ) : (
                  !loading && <p className="text-muted">No hay resultados para mostrar</p>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
        
        <ClientSearchModal
          show={showClientModal}
          onHide={() => {
            setShowClientModal(false);
            setClientModalFilterIndex(null);
          }}
          onClientSelect={handleClientSelect}
          excludeDisabled={true}
        />
      </Container>
    </div>
  );
};

export default AnalyticsPage;